import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import {
  getConversations,
  getCachedConversations,
  getMessages,
  cacheMessages,
  formatMessagePreview,
  subscribeToMessages,
} from '../utils/messages';
import { getCachedUser } from '../utils/auth';
import { subscribeWithAuth } from '../utils/supabaseClient';
import ChatWindow from './ChatWindow';
import { ConversationSkeleton } from './common/Skeleton';
import '../styles/MessagesPage.css';

const MessagesTab = () => {
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [browserNotifications, setBrowserNotifications] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const currentUser = getCachedUser();

  useEffect(() => {
    // Check browser notification permission
    if ('Notification' in window) {
      setBrowserNotifications(Notification.permission === 'granted');
    }

    // Load from cache immediately for instant display
    const cached = getCachedConversations();
    if (cached && cached.length > 0) {
      setConversations(cached);
      setFilteredConversations(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }

    // Then load fresh data in background
    loadConversations();
    
    // Check for conversation ID in URL params to auto-select
    const conversationIdFromUrl = searchParams.get('conversation');
    if (conversationIdFromUrl) {
      const selectConversationFromUrl = () => {
        const cached = getCachedConversations();
        if (cached && cached.length > 0) {
          const conv = cached.find(c => c.id === conversationIdFromUrl);
          if (conv) {
            setSelectedConversation(conv);
          }
        }
      };
      
      selectConversationFromUrl();
      setTimeout(selectConversationFromUrl, 500);
    }
    
    const unsubscribeInbox = currentUser?.id
      ? subscribeWithAuth(`messages_tab_global_${currentUser.id}`, [
          {
            event: 'INSERT',
            table: 'messages',
            filter: `receiver_id=eq.${currentUser.id}`,
            handler: (payload) => {
              if (payload?.new) loadConversations(false);
            },
          },
        ])
      : () => {};

    return () => {
      unsubscribeInbox();
    };
  }, [currentUser?.id, searchParams]);

  useEffect(() => {
    if (!selectedConversation?.id) {
      setMessages([]);
      return undefined;
    }

    const conversationId = selectedConversation.id;
    let cancelled = false;
    setMessagesLoading(true);
    getMessages(conversationId, false).then(({ data }) => {
      if (cancelled) return;
      setMessages(data || []);
      if (data) cacheMessages(conversationId, data);
      setMessagesLoading(false);
    });

    const unsubscribe = subscribeToMessages(conversationId, (incoming) => {
      setMessages((prev) => {
        if (prev.some((item) => item.id === incoming.id)) return prev;
        const updated = [...prev, incoming];
        cacheMessages(conversationId, updated);
        return updated;
      });
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [selectedConversation?.id]);

  // Filter conversations based on category, unread, and search
  useEffect(() => {
    let filtered = [...conversations];

    // Filter by category
    if (activeCategory !== 'All') {
      filtered = filtered.filter(conv => conv.category === activeCategory);
    }

    // Filter by unread only
    if (showUnreadOnly) {
      filtered = filtered.filter(conv => conv.unread_count > 0);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(conv => {
        const otherParty = getOtherParty(conv);
        const username = otherParty?.username || '';
        const preview = conv.last_message_preview || '';
        const context = conv.request?.title || conv.listing?.title || '';
        return (
          username.toLowerCase().includes(query) ||
          preview.toLowerCase().includes(query) ||
          context.toLowerCase().includes(query)
        );
      });
    }

    // Sort by last message time
    filtered.sort((a, b) => {
      const timeA = new Date(a.last_message_at || a.created_at).getTime();
      const timeB = new Date(b.last_message_at || b.created_at).getTime();
      return timeB - timeA;
    });

    setFilteredConversations(filtered);
  }, [conversations, activeCategory, showUnreadOnly, searchQuery]);

  const loadConversations = async (useCache = true) => {
    if (useCache) {
      const cached = getCachedConversations();
      if (cached) {
        setConversations(cached);
        setLoading(false);
        loadConversations(false);
        return;
      }
    }

    setLoading(true);
    try {
      const { data, error } = await getConversations(useCache);
      if (error) {
        console.error('Failed to load conversations:', error);
        if (!useCache) {
          setConversations([]);
        }
      } else {
        // getConversations already deduplicates, so we can use data directly
        const deduplicated = data || [];
        setConversations(deduplicated);
        
        const conversationIdFromUrl = searchParams.get('conversation');
        if (conversationIdFromUrl && !selectedConversation) {
          const conv = deduplicated.find(c => c.id === conversationIdFromUrl);
          if (conv) {
            setSelectedConversation(conv);
          }
        }
        
        if (typeof localStorage !== 'undefined') {
          try {
            localStorage.setItem('conversations', JSON.stringify(deduplicated));
          } catch (e) {
            // Ignore cache errors
          }
        }
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
      if (!useCache) {
        setConversations([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const getOtherParty = (conversation) => {
    if (conversation.user_id === currentUser?.id) {
      return conversation.seller;
    }
    return conversation.user;
  };


  const handleMarkAllAsRead = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    try {
      const response = await fetch('/api/messages/mark-all-read', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        loadConversations(false);
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleBrowserNotificationsToggle = async () => {
    if (!('Notification' in window)) {
      alert('Browser notifications are not supported in this browser');
      return;
    }

    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      setBrowserNotifications(permission === 'granted');
    } else if (Notification.permission === 'granted') {
      setBrowserNotifications(false);
    }
  };

  const getAvatarDisplay = (user) => {
    if (!user) return null;

    const cachedAvatar = user.id ? localStorage.getItem(`avatar_${user.id}`) : null;
    if (cachedAvatar) {
      return <img src={cachedAvatar} alt={user.username || 'User'} className="conversation-avatar-img" />;
    }

    if (user.avatar_url) {
      if (user.avatar_url.startsWith('letter:')) {
        const letter = user.avatar_url.split(':')[1] || (user.username || 'U')[0].toUpperCase();
        return <div className="conversation-avatar-letter">{letter}</div>;
      }
      return <img src={user.avatar_url} alt={user.username || 'User'} className="conversation-avatar-img" />;
    }

    const letter = (user.username || 'U')[0].toUpperCase();
    return <div className="conversation-avatar-letter">{letter}</div>;
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays === 1) return '1d';
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const categories = ['All', 'Boosting', 'Orders', 'Support', 'Pre-purchase'];

  // Show skeleton loading only if we have no cached data
  const showSkeleton = loading && conversations.length === 0;

  return (
    <div className="profile-messages-section">
      <div className={`messages-container ${selectedConversation ? 'show-chat' : ''}`}>
        <div className="messages-sidebar">
          <div className="messages-filters">
            <div className="category-tabs">
              {categories.map(category => (
                <button
                  key={category}
                  type="button"
                  className={`category-tab ${activeCategory === category ? 'active' : ''}`}
                  onClick={() => setActiveCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>

            <div className="filter-options">
              <div className="filter-actions">
                <div className="filter-option">
                  <label htmlFor="unread-toggle">Unread only</label>
                  <input
                    type="checkbox"
                    id="unread-toggle"
                    checked={showUnreadOnly}
                    onChange={(e) => setShowUnreadOnly(e.target.checked)}
                    className="toggle-switch"
                  />
                </div>

                <div className="filter-option">
                  <label htmlFor="browser-notifications-toggle">Notifications</label>
                  <input
                    type="checkbox"
                    id="browser-notifications-toggle"
                    checked={browserNotifications}
                    onChange={handleBrowserNotificationsToggle}
                    className="toggle-switch"
                  />
                </div>

                <button type="button" className="mark-all-read-link" onClick={handleMarkAllAsRead}>
                  Mark all as read
                </button>
              </div>

              <div className="search-bar">
                <Search size={14} className="search-icon" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
              </div>
            </div>
          </div>

          <div className="conversations-list">
            {showSkeleton ? (
              <div className="conversations-list-skeleton">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <ConversationSkeleton key={i} />
                ))}
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="no-conversations">
                <p>No conversations found.</p>
              </div>
            ) : (
              filteredConversations.map((conversation) => {
                const otherParty = getOtherParty(conversation);
                const hasUnread = conversation.unread_count > 0;
                const lastMessage = formatMessagePreview(conversation.last_message_preview || '');
                
                return (
                  <div
                    key={conversation.id}
                    className={`conversation-item ${selectedConversation?.id === conversation.id ? 'active' : ''} ${hasUnread ? 'unread' : ''}`}
                    onClick={() => setSelectedConversation(conversation)}
                  >
                    <div className="conversation-avatar">
                      {getAvatarDisplay(otherParty)}
                      {hasUnread && <div className="unread-dot"></div>}
                    </div>
                    <div className="conversation-info">
                      <div className="conversation-header">
                        <span className="conversation-name">{otherParty?.username || 'User'}</span>
                        <span className="conversation-time">{formatTime(conversation.last_message_at)}</span>
                      </div>
                      {/* Threads are per request, so the same person can appear more than once. */}
                      {(conversation.request?.title || conversation.listing?.title) && (
                        <div className="conversation-context" title={conversation.request?.title || conversation.listing?.title}>
                          {conversation.request?.title || conversation.listing?.title}
                        </div>
                      )}
                      <div className="conversation-preview">
                        {lastMessage.length > 50 ? `${lastMessage.substring(0, 50)}...` : lastMessage}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="messages-main">
          {selectedConversation ? (
            <ChatWindow
              key={selectedConversation.id}
              conversation={selectedConversation}
              messages={messages}
              messagesLoading={messagesLoading}
              onClose={() => setSelectedConversation(null)}
              embedded={true}
            />
          ) : (
            <div className="messages-placeholder">
              <p>Select a conversation to start messaging</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessagesTab;

